import { useCallback, useContext, useMemo, useRef } from 'react'

import { useQuery } from '@tanstack/react-query'
import moment from 'moment'
import { cache, useSWRInfinite } from 'swr'

import { MetricsApi } from 'api'
import { fetchPagedData } from 'api/AccountActivityAPI'
import { PagedResponse } from 'api/AccountActivityAPI/types'
import { DateRange } from 'components/DateRangePicker'
import { CurrentUserContext } from 'context/currentUserContext'
import { AccountActivitySortValue, Insight } from 'modules/accountActivity/domain'
import { insightTypeList } from 'sections/CCRM/FeedCard'
import { FilterPage } from 'utils/filters'
import { captureExceptionWithMessage } from 'utils/logRocket'

import { getCurrentState, PagedResponseData, useTransformedFilters } from './paginationAndApiUtils'

const DEFAULT_PAGE_SIZE = 25

let loadTimeMetricSent = false

const fetcherFn = (
  url: string,
  sortBy: AccountActivitySortValue,
  descending: boolean,
  searchQuery: string | undefined,
  dateRange: DateRange | undefined,
  stringifiedFilterGroups: string,
  accountId?: string,
  contactId?: string,
  hideAccountSummary?: boolean
) => {
  return fetchPagedData<Insight>({
    url,
    sortBy,
    descending,
    accountName: searchQuery,
    dateRange,
    filterGroups: JSON.parse(stringifiedFilterGroups),
    accountId,
    contactId,
    ...(hideAccountSummary && {
      insightTypes: insightTypeList.filter(insight => insight !== 'ACCOUNT_SUMMARY')
    })
  })
}

export const useInsightsFeed = (
  filterPage: FilterPage,
  selectedSort: AccountActivitySortValue,
  descending: boolean,
  pageSize: number = DEFAULT_PAGE_SIZE,
  withDateRange = false,
  withFilters = true,
  accountId?: string,
  contactId?: string,
  customDateRange?: DateRange,
  hideAccountSummary?: boolean
): {
  insights: PagedResponseData<Insight>
  refresh: () => void
} => {
  const currentUser = useContext(CurrentUserContext)
  const { searchQuery, dateRange, serializableFilterGroups } = useTransformedFilters(
    filterPage,
    currentUser?.id,
    customDateRange
  )

  const getKey = (index: number, previousPageData: PagedResponse<Insight> | null) => {
    if (previousPageData && !previousPageData.nextPagePath) {
      return null
    }

    return [
      previousPageData?.nextPagePath ??
        `/v2/insights/feed/?pageSize=${pageSize}&includeCount=false&includeOpps=false&includeScores=true`,
      selectedSort,
      descending,
      searchQuery,
      withDateRange ? dateRange : undefined,
      withFilters ? JSON.stringify(serializableFilterGroups) : '[]',
      accountId,
      contactId,
      hideAccountSummary,
      `$$FILTER_PAGE=${filterPage}$$`
    ]
  }

  const config = {
    revalidateOnMount: !cache.get(getKey(0, null)),
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupe: true
  }

  const timeOfInitialRender = useRef(window.performance?.now())

  const extendedConfig = {
    ...config,
    loadingTimeout: 30_000,
    onSuccess: () => {
      if (!loadTimeMetricSent) {
        MetricsApi.post('/monitoring/metrics/add', {
          value: window.performance.now() - timeOfInitialRender.current,
          metric: 'activity-groups-first-rows-displayed',
          dimensions: {
            type: 'bucket',
            buckets: '500,1000,2000,4000,8000'
          }
        })

        loadTimeMetricSent = true
      }
    },
    onLoadingSlow: () => {
      MetricsApi.post('/monitoring/metrics/add', {
        metric: `activity-groups-loading-slow`,
        value: 1,
        dimensions: {
          type: 'counter'
        }
      })
    },
    onError: (error: any) => {
      captureExceptionWithMessage('Error fetching insights', error, {
        url: window.location.href,
        userId: `${currentUser?.id}`
      })
      MetricsApi.post('/monitoring/metrics/add', {
        metric: `activity-groups-error`,
        value: 1,
        dimensions: {
          type: 'counter'
        }
      })
    }
  }

  //TODO @cmcgorrill update to use react-query when tackling the next-gen prospector UX
  const { data, error, mutate, size, setSize } = useSWRInfinite<PagedResponse<Insight>>(
    getKey,
    fetcherFn,
    filterPage === FilterPage.ACTIVITIES ? extendedConfig : config
  )

  // WORKAROUND: Remove any duplicate rows. This can happen because groups are
  // processed in real time (and are sorted by recent activity). A set of new
  // groups can push the offsets of existing ones down onto a subsequent page
  // of results.
  const dedupedData: PagedResponse<Insight>[] | undefined = data ? [] : undefined
  const seen: Set<number> = new Set()

  if (data && dedupedData) {
    for (const page of data) {
      const dedupedPageData = []

      for (const activityGroup of page.data) {
        if (!seen.has(activityGroup.id)) {
          seen.add(activityGroup.id)
          dedupedPageData.push(activityGroup)
        }
      }

      dedupedData.push({ ...page, data: dedupedPageData })
    }
  }

  const refresh = useCallback(() => {
    cache
      .keys()
      .filter(key => key.includes(`$$FILTER_PAGE=${filterPage}$$`))
      .forEach(key => cache.delete(key))

    // only reload the first page in the event that the user has scrolled
    if (setSize) {
      setSize(1)
    }

    // forces revalidation (refetching) of data + count
    mutate(null as any, true)
  }, [filterPage, mutate, setSize])

  return {
    insights: {
      ...getCurrentState<Insight>(dedupedData, error, size),
      error,
      mutate,
      size,
      setSize
    },
    refresh
  }
}

export const useRefreshingInsightsCount = (
  mostRecentActivityAt: number,
  filterPage: FilterPage,
  accountId?: string,
  contactId?: string
): number => {
  const currentUser = useContext(CurrentUserContext)
  const { searchQuery, serializableFilterGroups } = useTransformedFilters(
    filterPage,
    currentUser?.id
  )
  const dateRange = useMemo(
    () => ({
      startDate: moment(mostRecentActivityAt + 1),
      endDate: moment().add(1, 'day')
    }),
    [mostRecentActivityAt]
  )
  const key = [
    'COUNT/v2/insights/feed/?pageSize=10&includeCount=false&includeComments=false',
    searchQuery,
    dateRange,
    JSON.stringify(serializableFilterGroups),
    accountId,
    contactId
  ]
  const { data } = useQuery<PagedResponse<Insight>>({
    queryKey: key,
    queryFn: () =>
      fetcherFn(
        '/v2/insights/feed/?pageSize=10&includeCount=false&includeComments=false',
        AccountActivitySortValue.MOST_RECENT, // Sorting doesn't matter when getting the count
        true, // Same thing with sort direction
        searchQuery,
        dateRange,
        JSON.stringify(serializableFilterGroups),
        accountId,
        contactId
      ),
    staleTime: 30000
  })

  return data?.data?.length || 0
}
